/**
 * Tagada Rx — products page.
 *
 * Merchant view: treatments + editable price + enable. No clinical UUIDs.
 * Account managers can still open the mapping dialog.
 */

'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Settings2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { TableCard } from '@/components/ui/table-card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ProductTableImageCell } from '@/components/products/ProductTableImageCell';
import { RxPageShell } from '@/components/rx';
import { useToast } from '@/components/ui/use-toast';
import { useAccountManager } from '@/hooks/use-account-manager';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import {
  formatCents,
  useRxIntegrationStatus,
  useRxProductOfferings,
  useRxSetOfferingCost,
  useRxReviewAddon,
  useRxSetTreatmentActive,
  useRxSetTreatmentPrice,
  useRxStoreTreatments,
  type RxStoreTreatment,
} from '@/lib/api/rx';
import { ConfigureRxProductDialog } from './configure-rx-product-dialog';
import { WeightLossProgramCard } from './weight-loss-program-card';

const intervalSuffix = (interval: string | null): string => {
  if (interval === 'month') return '/mo';
  if (interval === 'year') return '/yr';
  if (interval === 'week') return '/wk';
  return '';
};

function TreatmentPriceCell({ row, onSaved }: { row: RxStoreTreatment; onSaved: () => void }) {
  const { t } = useTranslation('rx');
  const { toast } = useToast();
  const setPrice = useRxSetTreatmentPrice();
  const dollars = row.amountMinor != null ? (row.amountMinor / 100).toFixed(2) : '';
  const [draft, setDraft] = React.useState(dollars);

  React.useEffect(() => {
    setDraft(dollars);
  }, [dollars]);

  const commit = async () => {
    if (!row.priceId) return;
    const parsed = Number.parseFloat(draft);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setDraft(dollars);
      return;
    }
    const amountMinor = Math.round(parsed * 100);
    if (amountMinor === row.amountMinor) return;
    try {
      const saved = await setPrice.mutateAsync({
        storeId: row.storeId,
        priceId: row.priceId,
        amountMinor,
        currency: row.currency,
      });
      // Selling at a loss is allowed, never silent.
      if (saved.belowCost && saved.marginMinor != null) {
        toast({
          title: t('productOfferings.belowCost.toastTitle'),
          description: t('productOfferings.belowCost.toastDescription', {
            loss: formatCents(-saved.marginMinor, row.currency),
          }),
          variant: 'destructive',
        });
      }
      onSaved();
    } catch (err) {
      setDraft(dollars);
      toast({
        title: t('productOfferings.toasts.priceUpdateFailedTitle'),
        description: err instanceof Error ? err.message : t('productOfferings.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  if (!row.priceId) return <span className="text-muted-foreground">—</span>;

  const showRecommended =
    row.recommendedAmountMinor != null && row.recommendedAmountMinor !== row.amountMinor;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Input
          className="h-8 w-24"
          inputMode="decimal"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          disabled={setPrice.isPending}
          aria-label={t('productOfferings.ariaPrice', { name: row.treatmentName })}
        />
        <span className="text-muted-foreground text-xs">
          {row.currency}
          {intervalSuffix(row.interval)}
        </span>
      </div>
      {row.belowCost && row.marginMinor != null ? (
        <div className="text-destructive text-xs font-medium">
          {t('productOfferings.belowCost.inline', {
            loss: formatCents(-row.marginMinor, row.currency),
          })}
        </div>
      ) : null}
      {showRecommended ? (
        <div className="text-muted-foreground text-xs">
          {t('productOfferings.recommended', {
            price: formatCents(row.recommendedAmountMinor as number, row.currency),
          })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Pharmacy cost of the medication. Read-only for merchants (cost
 * transparency); inline-editable by account managers.
 */
function TreatmentCostCell({
  row,
  editable,
  onSaved,
}: {
  row: RxStoreTreatment;
  editable: boolean;
  onSaved: () => void;
}) {
  const { t } = useTranslation('rx');
  const { toast } = useToast();
  const setCost = useRxSetOfferingCost();
  const dollars = row.medicationCostMinor != null ? (row.medicationCostMinor / 100).toFixed(2) : '';
  const [draft, setDraft] = React.useState(dollars);

  React.useEffect(() => {
    setDraft(dollars);
  }, [dollars]);

  if (!row.isRx || !row.offeringId) return <span className="text-muted-foreground">—</span>;

  if (!editable) {
    return row.medicationCostMinor != null ? (
      <span className="font-medium">{formatCents(row.medicationCostMinor, row.costCurrency ?? 'USD')}</span>
    ) : (
      <span className="text-muted-foreground text-xs">{t('productOfferings.notConfigured')}</span>
    );
  }

  const commit = async () => {
    if (draft.trim() === '' && row.medicationCostMinor == null) return;
    const parsed = draft.trim() === '' ? null : Number.parseFloat(draft);
    if (parsed != null && (!Number.isFinite(parsed) || parsed < 0)) {
      setDraft(dollars);
      return;
    }
    const minor = parsed != null ? Math.round(parsed * 100) : null;
    if (minor === row.medicationCostMinor) return;
    try {
      await setCost.mutateAsync({
        offeringId: row.offeringId as string,
        medicationCostMinor: minor,
        costCurrency: row.costCurrency ?? 'USD',
      });
      onSaved();
    } catch (err) {
      setDraft(dollars);
      toast({
        title: t('productOfferings.toasts.medicationCostUpdateFailedTitle'),
        description: err instanceof Error ? err.message : t('productOfferings.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        className="h-8 w-24"
        inputMode="decimal"
        placeholder="0.00"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        disabled={setCost.isPending}
        aria-label={t('productOfferings.ariaMedicationCost', { name: row.treatmentName })}
      />
      <span className="text-muted-foreground text-xs">{row.costCurrency ?? 'USD'}</span>
    </div>
  );
}

export default function RxProductOfferingsPage() {
  const { t } = useTranslation('rx');
  const { id: storeId } = useStoreNavigate();
  const { isAccountManager } = useAccountManager();
  const { toast } = useToast();
  const integrationStatus = useRxIntegrationStatus(storeId);
  const treatmentsQuery = useRxStoreTreatments(storeId);
  const offeringsQuery = useRxProductOfferings();
  const setActive = useRxSetTreatmentActive();
  const reviewAddon = useRxReviewAddon();
  const { isSuperAdmin, isRxAdmin, whitelabelScope } = useSessionClaims();
  const isTagadaStaff = isSuperAdmin || isAccountManager || (isRxAdmin && !whitelabelScope);

  const [dialogRow, setDialogRow] = React.useState<RxStoreTreatment | null>(null);

  // Merchant-facing settings are vendor-scrubbed and omit the network slug.
  const activeSettings = integrationStatus.data?.state === 'active' ? integrationStatus.data.settings : null;
  const clinicalNetworkSlug =
    activeSettings && 'clinicalNetworkSlug' in activeSettings ? activeSettings.clinicalNetworkSlug : 'mdi';

  const offeringByKey = React.useMemo(() => {
    const map = new Map<string, NonNullable<typeof offeringsQuery.data>[number]>();
    for (const row of offeringsQuery.data ?? []) {
      map.set(`${row.productId}|${row.variantId ?? ''}`, row);
      if (!map.has(`${row.productId}|`)) map.set(`${row.productId}|`, row);
    }
    return map;
  }, [offeringsQuery.data]);

  const rows = treatmentsQuery.data?.treatments ?? [];
  const loading = treatmentsQuery.isLoading;
  const rxActive = integrationStatus.data?.state === 'active';

  const handleEnable = async (row: RxStoreTreatment, enabled: boolean) => {
    try {
      const result = await setActive.mutateAsync({
        storeId,
        productId: row.productId,
        variantId: row.variantId,
        enabled,
      });
      if (result.via === 'review') {
        toast({
          title: t('productOfferings.addonReview.requestSentTitle'),
          description: t('productOfferings.addonReview.requestSentDescription'),
        });
      }
      await treatmentsQuery.refetch();
    } catch (err) {
      toast({
        title: enabled
          ? t('productOfferings.toasts.enableFailedTitle')
          : t('productOfferings.toasts.disableFailedTitle'),
        description: err instanceof Error ? err.message : t('productOfferings.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  // Tagada staff: approve or reject an add-on a brand asked to turn on.
  const handleReview = async (row: RxStoreTreatment, decision: 'approve' | 'reject') => {
    let reason: string | undefined;
    if (decision === 'reject') {
      reason = window.prompt(t('productOfferings.addonReview.rejectPrompt'))?.trim();
      if (!reason) return;
    }
    try {
      await reviewAddon.mutateAsync({
        storeId,
        productId: row.productId,
        variantId: row.variantId,
        decision,
        reason,
      });
      toast({
        title:
          decision === 'approve'
            ? t('productOfferings.addonReview.approvedTitle')
            : t('productOfferings.addonReview.rejectedTitle'),
      });
      await treatmentsQuery.refetch();
    } catch (err) {
      toast({
        title: t('productOfferings.addonReview.reviewFailedTitle'),
        description: err instanceof Error ? err.message : t('productOfferings.toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <RxPageShell title={t('productOfferings.title')} description={t('productOfferings.description')}>
      {!rxActive ? (
        <div className="text-muted-foreground rounded-lg border px-4 py-6 text-sm">
          {t('productOfferings.notActive')}
        </div>
      ) : (
        <div className="space-y-6">
          <WeightLossProgramCard storeId={storeId} />
          <TableCard>
            {loading ? (
              <div className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('productOfferings.loading')}
              </div>
            ) : rows.length === 0 ? (
              <div className="text-muted-foreground p-6 text-sm">{t('productOfferings.empty')}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14">{t('productOfferings.columns.image')}</TableHead>
                    <TableHead>{t('productOfferings.columns.treatment')}</TableHead>
                    <TableHead>{t('productOfferings.columns.price')}</TableHead>
                    <TableHead>{t('productOfferings.columns.yourCost')}</TableHead>
                    <TableHead>{t('productOfferings.columns.enabled')}</TableHead>
                    {isAccountManager ? <TableHead className="w-32" /> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={`${row.productId}:${row.variantId ?? 'none'}`}>
                      <TableCell>
                        <ProductTableImageCell
                          src={row.imageUrl}
                          alt={row.treatmentName}
                          placeholderLabel={t('productOfferings.imagePlaceholder')}
                          zoomLabel={t('productOfferings.imageZoom')}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="min-w-0">
                          <div className="truncate font-medium">{row.treatmentName}</div>
                          <div className="mt-0.5 flex flex-wrap gap-1">
                            {row.isRx ? (
                              <Badge className="text-[10px] uppercase">{t('productOfferings.badgeRx')}</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[10px] uppercase">
                                {t('productOfferings.badgeAddOn')}
                              </Badge>
                            )}
                            {row.addonReview?.status === 'pending' ? (
                              <Badge variant="outline" className="text-[10px]">
                                {t('productOfferings.addonReview.pending')}
                              </Badge>
                            ) : null}
                            {row.addonReview?.status === 'rejected' && !row.enabled ? (
                              <Badge variant="destructive" className="text-[10px]">
                                {t('productOfferings.addonReview.rejected')}
                              </Badge>
                            ) : null}
                          </div>
                          {row.addonReview?.status === 'rejected' &&
                          row.addonReview.reason &&
                          !row.enabled ? (
                            <p className="text-muted-foreground mt-1 text-xs">{row.addonReview.reason}</p>
                          ) : null}
                          {isTagadaStaff && row.addonReview?.status === 'pending' ? (
                            <div className="mt-2 flex gap-2">
                              <Button
                                size="sm"
                                className="h-7"
                                disabled={reviewAddon.isPending}
                                onClick={() => void handleReview(row, 'approve')}
                              >
                                {t('productOfferings.addonReview.approve')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7"
                                disabled={reviewAddon.isPending}
                                onClick={() => void handleReview(row, 'reject')}
                              >
                                {t('productOfferings.addonReview.reject')}
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <TreatmentPriceCell
                          row={row}
                          onSaved={() => {
                            void treatmentsQuery.refetch();
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TreatmentCostCell
                          row={row}
                          editable={isAccountManager}
                          onSaved={() => {
                            void treatmentsQuery.refetch();
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={row.enabled}
                          disabled={
                            setActive.isPending || (!row.enabled && row.addonReview?.status === 'pending')
                          }
                          onCheckedChange={(checked) => void handleEnable(row, checked)}
                        />
                      </TableCell>
                      {isAccountManager ? (
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8"
                            onClick={() => setDialogRow(row)}
                          >
                            <Settings2 className="mr-1.5 h-3.5 w-3.5" />
                            {t('productOfferings.configure')}
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableCard>
        </div>
      )}

      {isAccountManager ? (
        <ConfigureRxProductDialog
          open={!!dialogRow}
          onOpenChange={(open) => {
            if (!open) setDialogRow(null);
          }}
          product={
            dialogRow
              ? { id: dialogRow.productId, name: dialogRow.treatmentName, image: dialogRow.imageUrl }
              : null
          }
          offering={
            dialogRow
              ? (offeringByKey.get(`${dialogRow.productId}|${dialogRow.variantId ?? ''}`) ??
                offeringByKey.get(`${dialogRow.productId}|`) ??
                null)
              : null
          }
          clinicalNetworkSlug={clinicalNetworkSlug}
          storeId={storeId}
          variantId={dialogRow?.variantId}
          onSaved={() => {
            void offeringsQuery.refetch();
            void treatmentsQuery.refetch();
          }}
        />
      ) : null}
    </RxPageShell>
  );
}
